import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BASE_URL } from '@/config/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Navbar } from '@/components/Navbar';
import { Loader2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

const authChannel = new BroadcastChannel('auth');

const UserDelete = () => {
    const [searchParams] = useSearchParams();
    const tokenValid = searchParams.get('token');
    const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [school_type, setSchoolType ] = useState<string | null>(null);
    const [isConfirmed, setIsConfirmed] = useState(false);
    const { t } = useLanguage();
    const { user, token } = useAuth();
    const { toast } = useToast();

    useEffect(() => {
        // Don't do anything if the auth token isn't loaded yet, or if there's no validation token in the URL.
        // This prevents setting premature errors.
        if (!token || !tokenValid) {
            setIsLoading(false); // Not loading if there are no tokens to check
            return;
        }

        const checkToken = async () => {
            setIsLoading(true);
            setError(null); 
            try {
                const response = await fetch(`${BASE_URL}/api/user/checkDelete`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        'Accept': 'application/json'
                    },
                    body: JSON.stringify({ token: tokenValid })
                });

                const data = await response.json(); 

                if (response.ok && data.valid === true) {
                    setIsValidToken(true);
                    setSchoolType(data.school_type || null); 
                } else {
                    setIsValidToken(false);
                    setError("Jeton invalide ou expiré.");
                }
            } catch (err) {
                setIsValidToken(false);
                setError("Une erreur s'est produite lors de la vérification.");
                console.error(err);
            }
            finally {
                setIsLoading(false);
            }
        };

        checkToken();
    }, [token, tokenValid]);

    const handleConfirmDelete = async () => {
        if (!token || !tokenValid) {
            setError("Jeton d'authentification ou de validation manquant.");
            return;
        }

        setIsDeleting(true);
        try {
            const response = await fetch(`${BASE_URL}/api/user/confirmDelete`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({ token: tokenValid })
            });

            if (response.ok) {
                setIsConfirmed(true);
                localStorage.removeItem('auth-token');
                authChannel.postMessage('FORCE_LOGOUT');
            } else {
                setError("Une erreur s'est produite lors de la suppression de votre compte.");
            }
        } catch (err) {
            setError("Une erreur s'est produite lors de la suppression de votre compte.");
            console.error(err);
        } finally {
            setIsDeleting(false);
        }
    };


    if (isLoading) {
        return <div className="container mx-auto p-4 text-center p-4"><Loader2 className="animate-spin" /></div>;
    }

    if (error) {
        return <div className="text-center p-4 text-red-500">{error}</div>;
    }

    if (isConfirmed) {
        return (
            <div className="container mx-auto p-4">
                <Card>
                    <CardHeader>
                        <CardTitle>{t('deleteAccount.confirmedDeleteTitle')}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p>{t('deleteAccount.deleteSuccessMessage')}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isValidToken) {
        return (
            <div className="min-h-screen flex flex-col">
                  <Navbar />
            <div className="flex-1 container py-8 mt-16">
               <Card className="border-2 border-red-200">
                    <CardHeader>
                        <CardTitle>{t('deleteAccount.confirmDeleteTitle')}</CardTitle>
                        <p className="mb-4">
                            {t('deleteAccount.confirmDeleteDesc')}
                        </p>
                    </CardHeader>
                    <CardContent>
                        <p className="mb-4">
                            {t('deleteAccount.lastConfirmation')}
                            <br />
                            {t('profile.accountType')} : {school_type === 'S' ? t('accounts.schoolAccount') : school_type === 'C' ? t('accounts.coachAccount') : t('common.unknown')}
                        <br />
                        </p>
                        <Button onClick={handleConfirmDelete} disabled={isDeleting} variant="destructive">
                            {isDeleting ? <><Loader2 className="animate-spin" /></> : t('deleteAccount.confirmDelete')}
                        </Button>
                    </CardContent>
                </Card>
            </div>
            </div>
        );
    }

    return null; 
};

export default UserDelete;
